import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, Star } from 'lucide-react';
import { SectionHeader } from './HowItWorksSection';

const testimonials = [
  {
    name: 'Roberto Cavalcanti',
    role: 'Investidor · São Paulo',
    avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop&crop=face&q=80',
    quote: 'Em poucos meses já apoiei dois negócios incríveis. As ideias chegam até mim, e eu só escolho as que mais combinam comigo.',
    color: 'blue',
    rating: 5,
  },
  {
    name: 'Mariana Souza',
    role: 'Dona da AgroTech Brasil',
    avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200&h=200&fit=crop&crop=face&q=80',
    quote: 'Eu tinha uma ideia há anos e não sabia por onde começar. Aqui encontrei pessoas que acreditaram em mim — e tudo mudou.',
    color: 'gold',
    rating: 5,
  },
  {
    name: 'Helena Martins',
    role: 'Investidora · Rio de Janeiro',
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop&crop=face&q=80',
    quote: 'Descobri histórias e negócios maravilhosos que eu jamais conheceria. Investir aqui é leve e tem propósito de verdade.',
    color: 'blue',
    rating: 5,
  },
  {
    name: 'Diego Almeida',
    role: 'Criador da EcoModa',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&crop=face&q=80',
    quote: 'É tudo muito direto e humano. Em uma semana já estava conversando com pessoas dispostas a apoiar minha marca.',
    color: 'gold',
    rating: 5,
  },
  {
    name: 'Camila Pires',
    role: 'Investidora · Minas Gerais',
    avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop&crop=face&q=80',
    quote: 'Cada ideia tem um rosto e uma história. Isso transforma o jeito de investir — me sinto fazendo parte de algo real.',
    color: 'blue',
    rating: 5,
  },
  {
    name: 'Pedro Henrique',
    role: 'Fundador da HealthSpark',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face&q=80',
    quote: 'Comecei pequeno, com um sonho. Hoje meu negócio cresceu graças às conexões que fiz aqui. Mudou minha vida.',
    color: 'gold',
    rating: 5,
  },
];

const TestimonialsSection = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="testimonials" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          gold
          eyebrow="Histórias reais"
          title={<>Pessoas reais, sonhos que <span className="gradient-text-gold">viraram realidade</span>.</>}
          subtitle="Veja quem já encontrou um investidor — ou um bom negócio — aqui."
        />

        {/* Marquee row */}
        <div className="relative mt-16 overflow-hidden" style={{ maskImage: 'linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent)', WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent)' }}>
          <div className="marquee-track flex gap-5 w-max">
            {[...testimonials, ...testimonials].map((t, i) => (
              <TestimonialCard key={i} t={t} />
            ))}
          </div>
        </div>

        {/* Featured carousel */}
        <div className="mt-12 relative max-w-3xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
              className="glass-card rounded-3xl p-8 sm:p-10 text-center relative overflow-hidden"
            >
              <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#4facfe]/10 rounded-full blur-3xl" />
              <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-[#ffa726]/10 rounded-full blur-3xl" />

              <Quote className="mx-auto text-[#4facfe]/40" size={40} />
              <p className="mt-5 text-lg sm:text-xl text-white leading-relaxed font-light">“{testimonials[index].quote}”</p>

              <div className="mt-7 flex items-center justify-center gap-4">
                <div className={`relative w-14 h-14 rounded-full overflow-hidden ring-2 ${
                  testimonials[index].color === 'gold' ? 'ring-[#ffa726]/60' : 'ring-[#4facfe]/60'
                }`} style={{
                  boxShadow: testimonials[index].color === 'gold'
                    ? '0 0 30px rgba(255,167,38,0.5)'
                    : '0 0 30px rgba(79,172,254,0.5)'
                }}>
                  <img src={testimonials[index].avatar} alt={testimonials[index].name} className="w-full h-full object-cover" />
                </div>
                <div className="text-left">
                  <div className="text-base font-semibold text-white">{testimonials[index].name}</div>
                  <div className="text-xs text-slate-400">{testimonials[index].role}</div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Dots */}
          <div className="mt-6 flex items-center justify-center gap-2">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === index ? 'w-8 bg-gradient-to-r from-[#4facfe] to-[#ffa726]' : 'w-1.5 bg-white/15 hover:bg-white/30'
                }`}
                aria-label={`Depoimento ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const TestimonialCard = ({ t }) => {
  const isGold = t.color === 'gold';
  return (
    <div className={`shrink-0 w-[300px] sm:w-[360px] glass-card rounded-2xl p-5 card-hover`}>
      <div className="flex items-center gap-3">
        <div className={`relative w-11 h-11 rounded-full overflow-hidden ring-2 ${
          isGold ? 'ring-[#ffa726]/50' : 'ring-[#4facfe]/50'
        }`} style={{
          boxShadow: isGold ? '0 0 18px rgba(255,167,38,0.4)' : '0 0 18px rgba(79,172,254,0.4)'
        }}>
          <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
        </div>
        <div>
          <div className="text-sm font-semibold text-white">{t.name}</div>
          <div className="text-[11px] text-slate-400">{t.role}</div>
        </div>
      </div>
      <div className="mt-3 flex gap-0.5">
        {Array.from({ length: t.rating }).map((_, i) => (
          <Star key={i} size={12} className="fill-[#ffd54f] text-[#ffd54f]" />
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-300 leading-relaxed line-clamp-4">“{t.quote}”</p>
    </div>
  );
};

export default TestimonialsSection;
